import { useState } from 'react';
import PropTypes from 'prop-types';
import { ArcherContainer, ArcherElement } from 'react-archer';
import classnames from 'classnames';
import * as R from 'ramda';
import TokenInfoTable from '../components/tokenInfoTable';
import styles from '../styles/module/Tokenizer.module.css';
import colorPalettte from '../utils/tokenizerColorPalettte';
import { TOKENIZER_DIC } from '../constants';

const mergedTokenDic = (tokens: TokenItem[]) => {
  const dicList = R.flatten(
    R.map((t) => t.explain?.sourceDictionaries || [], tokens),
  );

  return R.uniq(dicList);
};

export default function TokenizerExplainChart({
  data,
}: {
  data: TokenizerResponse;
}) {
  const [shouldInfoCardHover, setShouldInfoCardHover] = useState([false, -1]);
  const [shouldTokenHover, setShouldTokenHover] = useState([false, -1]);
  const tokens = data.analyzedTokens[0] || [];
  return (
    <ArcherContainer strokeColor='red'>
      <div className='tw-flex tw-items-center tw-justify-center tw-py-10  tw-mb-10'>
        {tokens.map((term, idx) => {
          const colorIdx = idx % colorPalettte.length;
          const color = colorPalettte[colorIdx];
          const hoveringIdx = shouldTokenHover[1];
          const hovering = hoveringIdx === idx && shouldTokenHover[0];
          const divClass = classnames(
            `tw-px-10 tw-py-2 eds-type--body-1 ${color.backgroundColor}`,
            {
              'tw-shadow-md tw-scale-110': hovering,
              'hover:tw-shadow-md hover:tw-scale-110': !hovering,
            },
          );
          return (
            <ArcherElement
              id={`root_${idx}`}
              key={`key-root_${idx}`}
              relations={[
                {
                  targetId: `leaf_${idx}`,
                  targetAnchor: 'top',
                  sourceAnchor: 'bottom',
                },
              ]}
            >
              <div
                className={divClass}
                onMouseEnter={() => setShouldInfoCardHover([true, idx])}
                onMouseLeave={() => setShouldInfoCardHover([false, -1])}
              >
                {term.tokenizedTerm}
              </div>
            </ArcherElement>
          );
        })}
      </div>
      <div className='tw-flex tw-items-center tw-justify-center flex-nowrap tw-py-10 tw-gap-2'>
        {tokens.map((term, idx) => {
          const colorIdx = idx % colorPalettte.length;
          const color = colorPalettte[colorIdx];
          const hoveringIdx = shouldInfoCardHover[1];
          const hovering = hoveringIdx === idx && shouldInfoCardHover[0];
          const hoverShadowClx = color.hoverShadowColor.replace('hover:', '');
          const divClass = classnames(
            `tw-w-px-162 tw-flex tw-justify-center tw-items-center tw-border-solid tw-rounded tw-border-2 ${color.borderColor} ${color.backgroundColor}  ${styles['token-info-card']}`,
            {
              [`tw-shadow-lg tw-scale-105 ${hoverShadowClx}`]: hovering,
              [`tw-shadow-md hover:tw-shadow-lg ${color.hoverShadowColor} hover:tw-scale-105`]:
                !hovering,
            },
          );
          const dic = term.explain.sourceDictionaries[0];
          return (
            <ArcherElement
              id={`leaf_${idx}`}
              key={`key-leaf_${idx}`}
              relations={[
                {
                  targetId: `bottom_${dic}`,
                  targetAnchor: 'top',
                  sourceAnchor: 'bottom',
                },
              ]}
            >
              <div
                className={divClass}
                onMouseEnter={() => setShouldTokenHover([true, idx])}
                onMouseLeave={() => setShouldTokenHover([false, -1])}
              >
                <TokenInfoTable token={term} />
              </div>
            </ArcherElement>
          );
        })}
      </div>
      <div className='tw-flex tw-items-center tw-justify-center flex-nowrap tw-py-10 tw-gap-2 tw-mt-6'>
        {mergedTokenDic(tokens).map((dic, idx) => {
          const colorIdx = colorPalettte.length - 2 - idx;
          const color = colorPalettte[colorIdx];
          const dicName = dic as DictionaryName;
          return (
            <ArcherElement id={`bottom_${dic}`} key={`key-bottom_${dic}`}>
              <div
                className={`tw-border-solid tw-rounded tw-border-2 tw-py-8 tw-px-12 ${color.borderColor} ${color.backgroundColor}`}
              >
                {TOKENIZER_DIC[dicName] || dic}
              </div>
            </ArcherElement>
          );
        })}
      </div>
    </ArcherContainer>
  );
}

TokenizerExplainChart.propTypes = {
  data: PropTypes.shape({
    analyzedTokens: PropTypes.arrayOf(PropTypes.object),
  }),
};
